/*
 * Copyright (c) 2018 Swizi byOpen.
 */
import axios from "axios";
import { path, omit } from "ramda";
import keycloak from "../../keycloak";

const errorData = (err) => ({
  message: path(["response", "data", "message"], err) || err.message,
  config: err.config,
  status: path(["response", "status"], err),
  code: path(["response", "status"], err) + "",
  data: path(["response", "data"], err),
});

class ProxyConnector {
  constructor() {
    this.baseURL = process.env.REACT_APP_PROXY_URL;

    this.AXIOS = axios.create({
      baseURL: `${this.baseURL}/api/`,
    });

    this.AXIOS.interceptors.request.use((config) => {
      if (keycloak.token) config.headers.Authorization = `Bearer ${keycloak.token}`;
      return config;
    });
  }

  /**
   |--------------------------------------------------
   | Apps
   |--------------------------------------------------
   */
  async listApps() {
    try {
      const res = await this.AXIOS.get(`/monitoring/apps`);
      return res.data;
    } catch (err) {
      console.error("Failed on listApps", errorData(err));
      throw omit(["config"], errorData(err));
    }
  }

  /**
   |--------------------------------------------------
   | Sites
   |--------------------------------------------------
   */
  async getSiteConf(siteId) {
    try {
      const res = await this.AXIOS.get(`/monitoring/sites/${siteId}/config`);
      return res.data;
    } catch (err) {
      console.error("Failed on getSiteConf", errorData(err));
      throw omit(["config"], errorData(err));
    }
  }
  async updateSiteConf(siteId, data) {
    try {
      const res = await this.AXIOS.put(`/sim/sites/${siteId}/config`, data);
      return res.data;
    } catch (err) {
      console.error("Failed on updateSiteConf", errorData(err));
      throw omit(["config"], errorData(err));
    }
  }

  /*
  |--------------------------------------------------------------------------
  | Spaces
  |--------------------------------------------------------------------------
  */
  async listSiteSpaces(siteId, { locale = "en" } = {}) {
    try {
      const res = await this.AXIOS.get(`/monitoring/sites/${siteId}/spaces`, {
        params: { locale },
      });
      return res.data;
    } catch (err) {
      console.error("Failed on listSiteSpaces", errorData(err));
      throw omit(["config"], errorData(err));
    }
  }
  async getSpaceDetails(siteId, spaceRef, { locale = "en" } = {}) {
    try {
      const res = await this.AXIOS.get(
        `/monitoring/sites/${siteId}/spaces/${encodeURIComponent(spaceRef)}`,
        { params: { locale: "en" } }
      );
      return res.data;
    } catch (err) {
      console.error("Failed on getSpaceDetails", errorData(err));
      throw omit(["config"], errorData(err));
    }
  }
  async updateSpace(siteId, spaceRef, data) {
    try {
      const res = await this.AXIOS.put(
        `/sim/sites/${siteId}/spaces/${encodeURIComponent(spaceRef)}`,
        data
      );
      return res.data;
    } catch (err) {
      console.error("Failed on updateSpace", errorData(err));
      throw omit(["config"], errorData(err));
    }
  }

  /*
  |--------------------------------------------------------------------------
  | Workplaces
  |--------------------------------------------------------------------------
  */
  async listWorkplaces(siteId, spaceId, { locale = "en" } = {}) {
    try {
      const res = await this.AXIOS.get(
        `/monitoring/sites/${siteId}/spaces/${encodeURIComponent(spaceId)}/workplaces`,
        { params: { locale } }
      );
      return res.data;
    } catch (err) {
      console.error("Failed on listWorkplaces", errorData(err));
      throw omit(["config"], errorData(err));
    }
  }
  async updateWorkplace(siteId, spaceId, workplaceId, data) {
    try {
      const res = await this.AXIOS.put(
        `/sim/sites/${siteId}/spaces/${encodeURIComponent(spaceId)}/workplaces/${encodeURIComponent(
          workplaceId
        )}`,
        data
      );
      return res.data;
    } catch (err) {
      console.error("Failed on updateWorkplace", errorData(err));
      throw omit(["config"], errorData(err));
    }
  }

  /*
  |--------------------------------------------------------------------------
  | Histories
  |--------------------------------------------------------------------------
  */
  async getOccupancy(siteId, spaceId, criterias) {
    try {
      const res = await this.AXIOS.post(
        `/history/sites/${siteId}/spaces/${encodeURIComponent(spaceId)}/occupancy`,
        criterias
      );
      return res.data;
    } catch (err) {
      console.error("Failed on getOccupancy", errorData(err));
      throw omit(["config"], errorData(err));
    }
  }
  async commandHistories(siteId, spaceId, criterias) {
    try {
      const res = await this.AXIOS.post(
        `/history/sites/${siteId}/spaces/${encodeURIComponent(spaceId)}/control`,
        criterias
      );
      return res.data;
    } catch (err) {
      console.error("Failed on commandHistories", errorData(err));
      throw omit(["config"], errorData(err));
    }
  }
  async webhookHistories(siteId, spaceId, criterias) {
    try {
      const url = spaceId
        ? `/history/sites/${siteId}/spaces/${encodeURIComponent(spaceId)}/webhooks`
        : `/history/sites/${siteId}/control`;

      const res = await this.AXIOS.post(url, criterias);
      return res.data;
    } catch (err) {
      console.error("Failed on webhookHistories", errorData(err));
      throw omit(["config"], errorData(err));
    }
  }
  async workplaceWebhookHistories(siteId, spaceId, criterias) {
    try {
      const res = await this.AXIOS.post(
        `/history/sites/${siteId}/spaces/${encodeURIComponent(spaceId)}/workplaces/webhooks`,
        criterias
      );
      return res.data;
    } catch (err) {
      console.error("Failed on workplaceWebhookHistories", errorData(err));
      throw omit(["config"], errorData(err));
    }
  }
  async getWorkplacesOccupancy(siteId, spaceId, criterias) {
    try {
      const res = await this.AXIOS.post(
        `/history/sites/${siteId}/spaces/${encodeURIComponent(spaceId)}/workplaces/occupancy`,
        criterias
      );
      return res.data;
    } catch (err) {
      console.error("Failed on getWorkplacesOccupancy", errorData(err));
      throw omit(["config"], errorData(err));
    }
  }
  async getCountingHistory(siteId, spaceId, criterias) {
    try {
      const res = await this.AXIOS.post(
        `/history/sites/${siteId}/spaces/${encodeURIComponent(spaceId)}/counting`,
        criterias
      );
      return res.data;
    } catch (err) {
      console.error("Failed on getCountingHistory", errorData(err));
      throw omit(["config"], errorData(err));
    }
  }
  async getCountingHookHistory(siteId, spaceId, criterias) {
    try {
      const res = await this.AXIOS.post(
        `/history/sites/${siteId}/spaces/${encodeURIComponent(spaceId)}/counting/webhooks`,
        criterias
      );
      return res.data;
    } catch (err) {
      console.error("Failed on getCountingHookHistory", errorData(err));
      throw omit(["config"], errorData(err));
    }
  }
}

export default new ProxyConnector();
